import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ChatBubbleLeftRightIcon,
  DevicePhoneMobileIcon,
  RocketLaunchIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import React from "react";

import Spinner from "components/atoms/Spinner";

import { StudioIntent } from "utils/easyaiStudio";

interface EasyAIStudioCardProps {
  onLaunch: (intent: StudioIntent) => void;
  /** Intent currently being launched, so only that button shows a spinner. */
  launchingIntent: StudioIntent | null;
  /** Whether we have a license key to hand to the Studio. */
  hasLicenseKey: boolean;
}

type StudioAction = {
  intent: StudioIntent;
  label: string;
  description: string;
  icon: typeof RocketLaunchIcon;
};

const SECONDARY_ACTIONS: StudioAction[] = [
  {
    intent: "continueApp",
    label: "Continue an app",
    description:
      "Jump straight back into the playground and keep building something you already started.",
    icon: ArrowPathIcon,
  },
  {
    intent: "askApi",
    label: "Ask how the API works",
    description:
      "Which route fits your use case, what a payload looks like, how auth and tokens behave.",
    icon: ChatBubbleLeftRightIcon,
  },
  {
    intent: "sdkHelp",
    label: "Mobile SDK help",
    description:
      "iOS, Android, React Native and Flutter: setup, proxy mode, and the v2 to v3 migration.",
    icon: DevicePhoneMobileIcon,
  },
];

const EasyAIStudioCard: React.FC<EasyAIStudioCardProps> = ({
  onLaunch,
  launchingIntent,
  hasLicenseKey,
}) => {
  const isLaunching = (intent: StudioIntent) => launchingIntent === intent;

  return (
    <div className="overflow-hidden rounded-md border border-indigo-200 shadow-lg">
      {/* --- Headline band --- */}
      <div className="relative bg-gradient-to-r from-[#312e81] via-[#4338ca] via-60% to-[#4d7c0f] px-6 py-6 sm:px-8 sm:py-7">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-10 -top-16 h-52 w-52 rounded-full bg-white/10 blur-2xl"
        />
        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-0.5 text-sm font-bold uppercase tracking-wide text-white">
              <SparklesIcon className="h-3.5 w-3.5" />
              EasyAI Studio
              <span className="ml-1 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 px-1.5 py-0.5 text-[10px] leading-none">
                NEW
              </span>
            </span>
            <h3 className="mt-3 text-xl font-semibold text-white sm:text-2xl">
              Let the Studio build your integration for you
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-indigo-100">
              The Studio already knows the whole Nutrition AI API — every route,
              the auth headers, the payload shapes, and what tokens each call
              costs. Describe what you want in plain English and it writes,
              hosts, and runs a working app against your own account. It is the
              fastest way to get started, and the best place to ask questions
              while you build.
            </p>
          </div>
          <div className="flex-shrink-0">
            <button
              type="button"
              onClick={() => onLaunch("newApp")}
              disabled={launchingIntent !== null}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-white px-5 py-3 text-sm font-semibold text-indigo-700 shadow-sm transition-colors hover:bg-indigo-50 disabled:cursor-wait disabled:opacity-70 lg:w-auto"
            >
              {isLaunching("newApp") ? (
                <Spinner className="!mr-0 !h-5 !w-5" />
              ) : (
                <RocketLaunchIcon className="h-5 w-5" />
              )}
              Start building an app
              <ArrowTopRightOnSquareIcon className="h-4 w-4 opacity-70" />
            </button>
            <p className="mt-2 text-center text-sm text-white/80 lg:text-right">
              Opens in a new tab, already signed in
            </p>
          </div>
        </div>
      </div>

      {/* --- Secondary entry points --- */}
      {/* One column, then three. A 2-column step would leave the third tile
          orphaned next to an empty cell, which shows through as a grey hole
          because the gap is painted by the container background. */}
      <div className="grid gap-px bg-gray-200 lg:grid-cols-3">
        {SECONDARY_ACTIONS.map(({ intent, label, description, icon: Icon }) => (
          <button
            key={intent}
            type="button"
            onClick={() => onLaunch(intent)}
            disabled={launchingIntent !== null}
            className="group flex items-start gap-3 bg-white p-4 text-left transition-colors hover:bg-indigo-50/60 disabled:cursor-wait disabled:opacity-70 sm:p-5"
          >
            <span className="mt-0.5 flex-shrink-0 rounded-lg bg-indigo-50 p-2 text-indigo-700 group-hover:bg-indigo-100">
              {isLaunching(intent) ? (
                <Spinner className="!mr-0 !h-5 !w-5" />
              ) : (
                <Icon className="h-5 w-5" />
              )}
            </span>
            <span className="min-w-0">
              <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                {label}
                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 text-gray-400" />
              </span>
              <span className="mt-1 block text-sm text-gray-600">
                {description}
              </span>
            </span>
          </button>
        ))}
      </div>

      {/* Deliberately does not promise the key lets the Studio call the API:
          that is true of the API intents, but in SDK mode nothing server-side
          consumes it and generated code ships a YOUR_PASSIO_KEY placeholder
          instead. */}
      {hasLicenseKey && (
        <p className="border-t border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-500 sm:px-5">
          Your API key is copied to the clipboard on launch, so you can paste it
          straight into the Studio&apos;s license step.
        </p>
      )}
    </div>
  );
};

export default EasyAIStudioCard;
